import { CHILEAN_COUNTRY_CODE, CHILEAN_LOCALE, CLP_CURRENCY, CURRENCY_MIN_FRACTION_DIGITS, CURRENCY_STYLE, ELLIPSIS, PHONE_LENGTH_WITH_COUNTRY, PHONE_LENGTH_WITHOUT_COUNTRY, SNAKE_CASE_SEPARATOR, SPACE_SEPARATOR, UF_MAX_FRACTION_DIGITS, UF_MIN_FRACTION_DIGITS, } from './format.utils.constants.js';
/**
 * Converts a camelCase string to snake_case
 */
export function camelCaseToSnakeCase(text) {
    return text.replace(/([A-Z])/g, `${SNAKE_CASE_SEPARATOR}$1`).toLowerCase();
}
/**
 * Formats a number to Chilean currency (CLP)
 */
export function formatCurrency(amount) {
    return new Intl.NumberFormat(CHILEAN_LOCALE, {
        currency: CLP_CURRENCY,
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        style: CURRENCY_STYLE,
    }).format(amount);
}
/**
 * Formats a number using a persisted property currency code
 */
export function formatCurrencyByCode(amount, currency) {
    if (currency === 'UF') {
        return formatUF(amount);
    }
    return new Intl.NumberFormat(CHILEAN_LOCALE, {
        currency: currency || CLP_CURRENCY,
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        style: CURRENCY_STYLE,
    }).format(amount);
}
/**
 * Formats a phone number
 */
export function formatPhoneNumber(phone) {
    // Remove non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    // Format as +56 9 1234 5678
    if (cleaned.length === PHONE_LENGTH_WITH_COUNTRY && cleaned.startsWith(CHILEAN_COUNTRY_CODE)) {
        return `+${cleaned.slice(0, 2)}${SPACE_SEPARATOR}${cleaned.slice(2, 3)}${SPACE_SEPARATOR}${cleaned.slice(3, 7)}${SPACE_SEPARATOR}${cleaned.slice(7)}`;
    }
    // Format as 9 1234 5678
    if (cleaned.length === PHONE_LENGTH_WITHOUT_COUNTRY) {
        return `${cleaned.slice(0, 1)}${SPACE_SEPARATOR}${cleaned.slice(1, 5)}${SPACE_SEPARATOR}${cleaned.slice(5)}`;
    }
    return phone;
}
/**
 * Formats a number to UF (Unidad de Fomento)
 */
export function formatUF(amount) {
    return `UF ${new Intl.NumberFormat(CHILEAN_LOCALE, {
        maximumFractionDigits: UF_MAX_FRACTION_DIGITS,
        minimumFractionDigits: UF_MIN_FRACTION_DIGITS,
    }).format(amount)}`;
}
/**
 * Recursively converts all keys of an object from snake_case to camelCase
 */
export function keysToCamelCase(object) {
    if (typeof object !== 'object' || object === null) {
        return object;
    }
    if (Array.isArray(object)) {
        return object.map((item) => keysToCamelCase(item));
    }
    const result = {};
    for (const [key, value] of Object.entries(object)) {
        if (typeof value === 'object' && value !== null) {
            result[snakeCaseToCamelCase(key)] = keysToCamelCase(value);
        }
        else {
            result[snakeCaseToCamelCase(key)] = value;
        }
    }
    return result;
}
/**
 * Recursively converts all keys of an object from camelCase to snake_case
 */
export function keysToSnakeCase(object) {
    if (typeof object !== 'object' || object === null) {
        return object;
    }
    if (Array.isArray(object)) {
        return object.map((item) => keysToSnakeCase(item));
    }
    const result = {};
    for (const [key, value] of Object.entries(object)) {
        if (typeof value === 'object' && value !== null) {
            result[camelCaseToSnakeCase(key)] = keysToSnakeCase(value);
        }
        else {
            result[camelCaseToSnakeCase(key)] = value;
        }
    }
    return result;
}
/**
 * Converts a snake_case string to camelCase
 */
export function snakeCaseToCamelCase(text) {
    return text.replace(new RegExp(`${SNAKE_CASE_SEPARATOR}([a-z])`, 'g'), (_, letter) => letter.toUpperCase());
}
/**
 * Capitalizes first letter of each word
 */
export function titleCase(text) {
    return text
        .toLowerCase()
        .split(SPACE_SEPARATOR)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(SPACE_SEPARATOR);
}
/**
 * Truncates text with ellipsis
 */
export function truncate(text, maxLength) {
    if (text.length <= maxLength)
        return text;
    return text.slice(0, maxLength) + ELLIPSIS;
}
